import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { Button } from "@/components/ui/button";
import { 
    Download, 
    ChevronDown, 
    Wrench,
    FileText,
    BrainCircuit,
    History,
    Contact,
    Building2,
    Check,
    ArrowRight
} from "lucide-react";
import GlassCard from "@/components/GlassCard";

const Features = () => {
    const { isStandalone, promptInstall } = usePWAInstall();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const handleInstallClick = async () => {
        await promptInstall();
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
    };

    return (
        <div className="min-h-screen w-full bg-[#F8FAF9] text-slate-900 font-sans overflow-x-hidden selection:bg-emerald-500/30 pb-32">
            {/* Background Glows (Optimized for performance) */}
            <div className="fixed top-0 left-[-10%] w-[40%] h-[40%] rounded-full pointer-events-none z-0" style={{ background: 'radial-gradient(circle, rgba(110,231,183,0.15) 0%, rgba(248,250,249,0) 70%)' }} />
            <div className="fixed top-40 right-[-10%] w-[40%] h-[40%] rounded-full pointer-events-none z-0" style={{ background: 'radial-gradient(circle, rgba(94,234,212,0.15) 0%, rgba(248,250,249,0) 70%)' }} />

            {/* Navbar */}
            <nav className="fixed top-0 left-0 right-0 z-50 px-6 py-4 border-b border-slate-200/50 bg-white/70 backdrop-blur-xl">
                <div className="max-w-6xl mx-auto flex items-center justify-between relative">
                    <div className="flex items-center gap-3 relative">
                        <Link 
                            to="/site"
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity outline-none"
                        >
                            <img src="/logo.png" alt="HomeEzi" className="w-8 h-8 rounded-lg" />
                            <span className="text-xl font-bold tracking-tight text-emerald-950">HomeEzi</span>
                        </Link>

                        <button 
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="flex items-center p-1.5 hover:opacity-80 transition-opacity outline-none bg-slate-100 rounded-md md:hidden"
                        >
                            <ChevronDown className={`w-4 h-4 text-emerald-950 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} />
                        </button>

                        <div className="hidden md:flex items-center gap-6 ml-4 border-l border-slate-200 pl-6">
                            <Link to="/features" className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors">
                                Features
                            </Link>
                            <a href="/site#pricing" className="text-sm font-semibold text-slate-600 hover:text-emerald-700 transition-colors">
                                Pricing
                            </a>
                        </div>

                        <AnimatePresence>
                            {isMenuOpen && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="absolute top-12 left-0 w-48 bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden flex flex-col py-2 z-50 md:hidden"
                                >
                                    <Link to="/features" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 text-sm font-semibold text-emerald-700 bg-emerald-50 transition-colors">
                                        Features
                                    </Link>
                                    <a href="/site#pricing" onClick={() => setIsMenuOpen(false)} className="px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-emerald-700 transition-colors">
                                        Pricing
                                    </a>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                    
                    {!isStandalone && (
                        <Button 
                            onClick={handleInstallClick}
                            className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full font-semibold px-5 shadow-[0_0_20px_rgba(52,211,153,0.3)] transition-all hover:scale-105 active:scale-95"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Add App
                        </Button>
                    )}
                </div>
            </nav>

            <main className="pt-32 relative z-10 max-w-6xl mx-auto px-6">
                
                {/* Header */}
                <div className="text-center space-y-6 mb-20">
                    <h1 className="text-5xl md:text-7xl font-extrabold tracking-tighter text-emerald-950">
                        Your home,<br/>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">pocket sized.</span>
                    </h1>
                    <p className="text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed">
                        HomeEzi replaces scattered spreadsheets, lost receipts, and forgotten maintenance schedules with one elegantly simple app.
                    </p>
                </div>

                {/* Bento Grid */}
                <motion.div 
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="grid grid-cols-1 md:grid-cols-2 gap-8"
                >
                    
                    {/* Feature 1: Maintenance */}
                    <motion.div variants={itemVariants}>
                        <GlassCard className="h-full p-8 md:p-12 bg-white/60 hover:bg-white/80 transition-colors border-slate-200/60 overflow-hidden relative group">
                            <div className="absolute -right-12 -top-12 w-64 h-64 bg-amber-100 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity" />
                            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-6 relative z-10">
                                <History className="w-7 h-7 text-amber-600" />
                            </div>
                            <h2 className="text-3xl font-bold text-slate-900 mb-4 relative z-10">Complete Maintenance History</h2>
                            <p className="text-lg text-slate-600 mb-8 max-w-md relative z-10">
                                Keep a permanent log of all your past services so you always know exactly when an appliance was last repaired.
                            </p>
                            <ul className="space-y-3 relative z-10">
                                {['Service Timeline', 'Store Invoices', 'Automatic Reminders to Providers'].map(item => (
                                    <li key={item} className="flex items-center gap-3 text-slate-700 font-medium">
                                        <div className="w-6 h-6 rounded-full bg-amber-50 flex items-center justify-center">
                                            <Check className="w-3.5 h-3.5 text-amber-500" />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </GlassCard>
                    </motion.div>

                    {/* Feature 2: Reminders */}
                    <motion.div variants={itemVariants}>
                        <GlassCard className="h-full p-8 md:p-12 bg-slate-900 text-white border-slate-800 overflow-hidden relative group">
                            <div className="absolute -left-12 -bottom-12 w-64 h-64 bg-teal-500 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" />
                            <div className="w-14 h-14 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center mb-6 relative z-10">
                                <BrainCircuit className="w-7 h-7 text-teal-400" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-4 relative z-10">Smart Service Reminders</h2>
                            <p className="text-lg text-slate-400 mb-8 relative z-10">
                                We help you keep up to date with services due by reminding you as services get close, as well as when to water your plants.
                            </p>
                            <ul className="space-y-3 relative z-10">
                                {['Recommended Actions', 'Automated Provider Alerts', 'Warranty Expirations'].map(item => (
                                    <li key={item} className="flex items-center gap-3 text-slate-200 font-medium">
                                        <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center">
                                            <Check className="w-3.5 h-3.5 text-teal-400" />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </GlassCard>
                    </motion.div>

                    {/* Feature 3: Provider Directory */}
                    <motion.div variants={itemVariants}>
                        <GlassCard className="h-full p-8 md:p-12 bg-blue-950 text-white border-blue-900 overflow-hidden relative group">
                            <div className="absolute right-0 top-0 w-64 h-64 bg-blue-500 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" />
                            <div className="w-14 h-14 rounded-2xl bg-blue-900/50 border border-blue-800 flex items-center justify-center mb-6 relative z-10 shadow-sm">
                                <Contact className="w-7 h-7 text-blue-400" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-4 relative z-10">Trusted Community Providers</h2>
                            <p className="text-lg text-blue-200/80 mb-8 max-w-md relative z-10">
                                Build your personal scope of trusted contractors or discover exactly who your neighbors are using for their repairs.
                            </p>
                            <ul className="space-y-3 relative z-10">
                                {['Building the Directory', 'Community Favourites', 'Grow the Community'].map(item => (
                                    <li key={item} className="flex items-center gap-3 text-blue-50 font-medium">
                                        <div className="w-6 h-6 rounded-full bg-blue-900/50 flex items-center justify-center shadow-sm">
                                            <Check className="w-3.5 h-3.5 text-blue-400" />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </GlassCard>
                    </motion.div>

                    {/* Feature 4: Document Vault */}
                    <motion.div variants={itemVariants}>
                        <GlassCard className="h-full p-8 md:p-12 bg-emerald-950 text-white border-emerald-900 overflow-hidden relative group">
                            <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-emerald-500 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity" />
                            <div className="w-14 h-14 rounded-2xl bg-emerald-900/50 border border-emerald-800 flex items-center justify-center mb-6 relative z-10">
                                <FileText className="w-7 h-7 text-emerald-400" />
                            </div>
                            <h2 className="text-3xl font-bold text-white mb-4 relative z-10">Complete Document Storage</h2>
                            <p className="text-lg text-emerald-100/80 mb-8 relative z-10">
                                Store purchase receipts, invoices, serial numbers, and warranty documents securely in the cloud. Access them instantly.
                            </p>
                            <ul className="space-y-3 relative z-10">
                                {['Invoice & Receipt Uploads', 'Warranty Tracking', 'General Home Records'].map(item => (
                                    <li key={item} className="flex items-center gap-3 text-emerald-50 font-medium">
                                        <div className="w-6 h-6 rounded-full bg-emerald-900/50 flex items-center justify-center">
                                            <Check className="w-3.5 h-3.5 text-emerald-400" />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </GlassCard>
                    </motion.div>

                </motion.div>

                {/* Call to Action bottom */}
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="mt-32 text-center"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Ready to simplify your home?</h2>
                    <Button 
                        onClick={handleInstallClick}
                        className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-full font-bold h-14 px-10 text-lg shadow-[0_20px_40px_rgba(52,211,153,0.3)] transition-transform hover:scale-105 active:scale-95"
                    >
                        Download the App
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </motion.div>
            </main>
        </div>
    );
};

export default Features;
